/** Premium vertical journey timeline — up to 7 days, auto progression. */

export const MAX_DAYS = 7
export const MIN_DAYS = 3
export const DAY_OPTIONS = [3, 4, 5, 6, 7]

export function newJourneyId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `j-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function createMilestone(title = '', notes = '') {
  return { id: newJourneyId(), title, notes }
}

export function defaultMilestones(count = 7) {
  const labels = [
    'Kickoff',
    'Research & scope',
    'Build core flow',
    'Polish & test',
    'Ship beta',
    'Gather feedback',
    'Launch',
  ]
  const n = Math.min(Math.max(count, MIN_DAYS), MAX_DAYS)
  return Array.from({ length: n }, (_, i) =>
    createMilestone(
      labels[i] ?? `Milestone ${i + 1}`,
      '',
    ),
  )
}

export function normalizeJourneyTimelineData(raw) {
  if (!raw || typeof raw !== 'object') return freshJourneyTimeline()

  if (Array.isArray(raw.events) && !raw.milestones) {
    return migrateLegacyEvents(raw)
  }

  const dayCount = clampDays(raw.dayCount ?? raw.milestones?.length ?? 7)

  let milestones = Array.isArray(raw.milestones)
    ? raw.milestones.slice(0, MAX_DAYS).map((m, i) => ({
        id: m.id ?? newJourneyId(),
        title: typeof m.title === 'string' ? m.title : `Day ${i + 1}`,
        notes: typeof m.notes === 'string' ? m.notes : m.description ?? '',
      }))
    : defaultMilestones(dayCount)

  milestones = resizeMilestones(milestones, dayCount)

  const status =
    raw.status === 'active' || raw.status === 'complete' ? raw.status : 'draft'

  return {
    title: typeof raw.title === 'string' ? raw.title : '',
    status,
    startedAt:
      typeof raw.startedAt === 'string' && raw.startedAt ? raw.startedAt : null,
    dayCount: milestones.length,
    expanded: Boolean(raw.expanded),
    milestones,
  }
}

export function freshJourneyTimeline(dayCount = 7) {
  const milestones = defaultMilestones(clampDays(dayCount))
  return {
    title: '',
    status: 'draft',
    startedAt: null,
    dayCount: milestones.length,
    expanded: false,
    milestones,
  }
}

function clampDays(n) {
  const v = Number(n)
  if (!Number.isFinite(v)) return 7
  return Math.min(MAX_DAYS, Math.max(MIN_DAYS, Math.round(v)))
}

function resizeMilestones(list, count) {
  const next = [...list]
  while (next.length < count) {
    next.push(createMilestone(`Day ${next.length + 1}`, ''))
  }
  return next.slice(0, count)
}

function migrateLegacyEvents(raw) {
  const milestones = (raw.events ?? []).slice(0, MAX_DAYS).map((e, i) =>
    createMilestone(
      typeof e.title === 'string' ? e.title : `Day ${i + 1}`,
      typeof e.description === 'string' ? e.description : '',
    ),
  )
  const count = Math.max(MIN_DAYS, milestones.length || 7)
  return {
    title: typeof raw.title === 'string' ? raw.title : '',
    status: 'draft',
    startedAt: null,
    dayCount: count,
    expanded: false,
    milestones: resizeMilestones(
      milestones.length ? milestones : defaultMilestones(count),
      count,
    ),
  }
}

export function startOfLocalDay(date = new Date()) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

/** Pill label e.g. "January, 2026" */
export function formatDatePill(date) {
  return date.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  })
}

export function daysSinceJourneyStart(startedAt, now = new Date()) {
  if (!startedAt) return 0
  const start = startOfLocalDay(new Date(startedAt))
  const today = startOfLocalDay(now)
  const diff = today.getTime() - start.getTime()
  if (diff < 0) return 0
  return Math.floor(diff / 86_400_000)
}

export function computeJourneyProgress(state, now = new Date()) {
  const milestones = state.milestones ?? []
  const total = milestones.length
  if (!total) {
    return {
      fillRatio: 0,
      dayIndex: 0,
      isComplete: false,
      dates: [],
      nodeStates: [],
    }
  }

  if (state.status === 'draft' || !state.startedAt) {
    return {
      fillRatio: 0,
      dayIndex: 0,
      isComplete: false,
      dates: milestones.map(() => null),
      nodeStates: milestones.map((_, i) => (i === 0 ? 'pending' : 'upcoming')),
    }
  }

  const start = startOfLocalDay(new Date(state.startedAt))
  const dates = milestones.map((_, i) => {
    const d = new Date(start)
    d.setDate(d.getDate() + i)
    return d
  })

  const elapsed = daysSinceJourneyStart(state.startedAt, now)
  const lastIndex = Math.max(0, total - 1)
  const dayIndex = Math.min(elapsed, lastIndex)
  const isComplete = elapsed >= lastIndex
  const todayStart = startOfLocalDay(now)
  const intraDay = (now.getTime() - todayStart.getTime()) / 86_400_000
  const fillRatio =
    lastIndex <= 0
      ? isComplete
        ? 1
        : intraDay
      : Math.min((elapsed + intraDay) / lastIndex, 1)

  const nodeStates = milestones.map((_, i) => {
    if (isComplete || i < dayIndex) return 'done'
    if (i === dayIndex) return 'active'
    return 'upcoming'
  })

  return {
    fillRatio,
    dayIndex,
    isComplete,
    dates,
    nodeStates,
  }
}
